"use client";

import { useEffect, useMemo, useRef, useState, useTransition } from "react";

import Logger from "@/src/exceptions/Logger";
import { IBackendAPIDataResponse } from "@/src/lib/axios/types";
import PrimaryButton from "@/src/shared/button/PrimaryButton";
import AppDialog from "@/src/shared/overlay/Dialog";
import _ from "lodash";
import { InputOtp } from "primereact/inputotp";
import { Toast } from "primereact/toast";
import * as motion from "motion/react-client";

interface CodeVerificationProps {
    open: boolean;
    email?: string;
    resendDelayTime?: number; //in seconds e.g. 30
    onResend?: () => Promise<IBackendAPIDataResponse | null>;
    onVerify: (code:string) => Promise<IBackendAPIDataResponse | null>;
    onHide?: (success?:boolean) => void;
}

export default function CodeVerification(props: CodeVerificationProps) {
    const { email, open=false, resendDelayTime = 30, onResend, onVerify, onHide } = props;

    const toast:any = useRef(null);
    const [code, setCode] = useState<any>("");
    const [resendCountDown, setResendCountDown] = useState<number>(0);
    const [resendTimer, setResendTimer] = useState<any>(null);

    const [processing, startProcessing] = useTransition();
    const [resending, startResending] = useTransition();

    const canResend = useMemo((): boolean => resendCountDown < 1, [resendCountDown]);

    useEffect(() => {
        if (canResend) {
            clearInterval(resendTimer)
        };

        return () => clearInterval(resendTimer);
    }, [canResend]);

    const verifyCode = () => {
        startProcessing(async () => {
            const res = await onVerify(code);
    
            if (res?.success) {
                handleHide(true);
            } else {
                toast.current.show({
                    severity: 'error', 
                    summary: res?.message, 
                    life: 3000
                });
            }
        })
    }

    const handleResendCode = () => {
        if (onResend) {
            startResending(async () => {
                const res: IBackendAPIDataResponse | null = await onResend();

                if (res?.success) {
                    handleShow();
                } else {
                    toast.current.show({
                        severity: 'error', 
                        summary: res?.message, 
                        life: 3000
                    });
                }
            })
        };
    }

    const inputTemp = ({events, props}: any) => {
        return (
            <input 
                key={props.id}
                {...events} 
                {...props} 
                className="input size-[50px] border border-gray-400 dark:border-gray-500 p-0"
            />
        );
    }

    const startResendTime = () => {
        setResendCountDown(resendDelayTime);

        let counter = resendDelayTime;

        const timer = setInterval(() => {
            if (counter >= 0) {
                setResendCountDown(counter);
            }

            Logger.info("Resend Counter: ", counter)

            counter = counter-1
        }, 1000);

        setResendTimer(timer);
        Logger.info("Resend Counter Set: ", timer);
    }

    const handleShow = () => {
        toast.current.show({
            severity: 'success', 
            summary: 'Veritication code sent', 
            life: 3000
        });

        startResendTime()
    }

    const handleHide = (success?:boolean) => {
        if(onHide) onHide(success);
        setCode("");
    }

    return (
        <>
            <Toast ref={toast} />
            <AppDialog
                open={open}
                // size="xs"
                // header={"Verify Code"}
                onHide={handleHide}
                onShow={handleShow}
            >
                <div>
                    <h4 className="text-2xl text-center font-medium mb-4">Verify Code</h4>
                    <p className="text-center px-6">
                        Please enter the verification code sent to your email <span className="bg-gray-300 text-xs text-gray-800 font-bold rounded-md py-1 px-2">{ email }</span>
                    </p>
                        
                    <div className="justify-self-center pt-10">
                        <InputOtp 
                            value={code}
                            length={6}
                            inputTemplate={inputTemp}
                            onChange={(e) => setCode(e.value)}
                        />
                    </div>

                    <div className="h-[50px] text-center pt-5">
                        {!canResend && (
                            // <span className="text-sm align-middle">
                            //     Resend Code in <span className="text-sky-500">{ resendCountDown }</span> seconds
                            // </span>
                            <motion.span
                                className="text-sm align-middle"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.3, ease: "easeInOut" }}
                            >
                                Resend Code in <span className="text-sky-500">{ resendCountDown }</span> seconds
                            </motion.span>
                        )}
                    </div>

                    <div className="flex justify-between gap-x-4 mt-20">
                        <PrimaryButton 
                            // scheme="default" 
                            className="transparent px-3 py-2"
                            disabled={!canResend}
                            loading={resending}
                            loadingtext="Resending code..."
                            onClick={handleResendCode}
                        >
                            Resend Code
                        </PrimaryButton>
                        <PrimaryButton 
                            className="px-3 py-2 font-light" 
                            type="button" 
                            // disabled={isPending}
                            loading={processing}
                            loadingtext="Verifying Code..."
                            onClick={verifyCode}
                        >
                            Verify
                        </PrimaryButton>
                    </div>
                </div>
            </AppDialog>
        </>
    );
}